import React, { type ComponentProps, useRef } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { BodyText, Icon, theme, useHovered, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderLocationInfo = (props: ContentProps) => {
  const { address, label, orderChannel, secondaryAddress } = props;

  return (
    <View style={styles.container}>
      <GroupOrderLocationInfoContent
        address={address}
        label={label}
        orderChannel={orderChannel}
        secondaryAddress={secondaryAddress}
      />
    </View>
  );
};

export const GroupOrderLocationInfoPressable = (props: PressableProps) => {
  const { accessibilityLabel, address, disabled, label, orderChannel, secondaryAddress, onPress } =
    props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const locationInfoRef = useRef(null);
  const isLocationInfoHovered = useHovered(locationInfoRef);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, locationInfoWebStyles];
  const backgroundStyles = [
    styles.background,
    locationInfoBgContainerWebStyles,
    isLocationInfoHovered ? styles.backgroundHovered : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={locationInfoRef}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      disabled={disabled}
      style={containerStyles}
      activeOpacity={0.75}
      onPress={onPress}
    >
      <View style={backgroundStyles} />

      <GroupOrderLocationInfoContent
        address={address}
        label={label}
        orderChannel={orderChannel}
        secondaryAddress={secondaryAddress}
      />

      <Icon name="IconCaretRight" />
    </TouchableOpacity>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const GroupOrderLocationInfoContent = (props: ContentProps) => {
  const { address, label, orderChannel, secondaryAddress } = props;

  const iconName = getLocationIconName(orderChannel);

  return (
    <View style={styles.labelContainer}>
      <Icon name={iconName} />

      <View style={styles.detailsContainer}>
        {label ? (
          <BodyText sizeMatch={['14']} bold>
            {label}
          </BodyText>
        ) : undefined}

        {address ? <BodyText sizeMatch={['14']}>{address}</BodyText> : undefined}

        {secondaryAddress ? <BodyText sizeMatch={['14']}>{secondaryAddress}</BodyText> : undefined}
      </View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getLocationIconName(orderChannel: OrderChannel | undefined): IconName {
  if (orderChannel === 'delivery') {
    return 'IconDelivery';
  }

  if (orderChannel === 'outpost' || orderChannel === 'pickup') {
    return 'IconPickupStrokeSm';
  }

  return 'IconLocationPin';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
    borderRadius: theme.radius.medium,
    marginTop: theme.spacing['2'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.medium,
    margin: -theme.spacing['2'],
    padding: theme.spacing['2'],
  },
  backgroundHovered: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  labelContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  detailsContainer: {
    flex: 1,
  },
});

const locationInfoWebStyles = webOnlyStyles({
  outlineOffset: 10,
  outlineColor: theme.colors.GRAY,
});

const locationInfoBgContainerWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms ease-in-out`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type PressableProps = ContentProps & {
  accessibilityLabel?: string;
  disabled?: boolean;
  onPress: () => void;
};

type ContentProps = {
  address?: string;
  label?: string;
  orderChannel?: OrderChannel;
  secondaryAddress?: string;
};

type IconName = ComponentProps<typeof Icon>['name'];
type OrderChannel = 'delivery' | 'outpost' | 'pickup';
